import Box from '@mui/material/Box';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { useCallback, useEffect, useRef } from 'react';

interface Props {
  lottieJSON: unknown;
  loop?: boolean;
  pxSize?: number;
  playIconAnimation?: number; // trigger from parent button click
  padding?: string;
  playOnHover?: boolean;
}

const LottiePlayer: React.FC<Props> = ({
  lottieJSON,
  loop = false,
  pxSize = -1,
  playIconAnimation = 0,
  padding = '0',
  playOnHover = false,
}) => {
  const ref = useRef<LottieRefCurrentProps | null>(null);

  // On load, go to frame 1 and stop so it's static
  useEffect(() => {
    if (!loop && ref !== null && ref.current !== null) {
      ref.current.goToAndStop(1, true);
    }
  }, [loop]);

  const handlePlay = useCallback(() => {
    if (!loop && ref !== null && ref.current !== null) {
      ref.current.goToAndPlay(1, true);
    }
  }, [loop]);

  const handleIconClick = () => {
    // play icon on click, if playIconAnimation prop has a value other than 0, parent controls animation in useEffect instead
    if (playIconAnimation === 0) {
      handlePlay();
    }
  };

  // If parent is clicked, a number is incremented to trigger play
  useEffect(() => {
    // Don't play on first load
    if (playIconAnimation !== 0) {
      handlePlay();
    }
  }, [playIconAnimation, handlePlay]);

  let size = '100%';
  if (pxSize !== -1) {
    size = `${pxSize}px`;
  }

  return (
    <Box
      sx={{ width: size, height: size }}
      onClick={handleIconClick}
      onMouseEnter={playOnHover ? handlePlay : undefined}
    >
      <Lottie
        style={{ width: '100%', height: '100%', padding: padding }}
        lottieRef={ref}
        animationData={lottieJSON}
        loop={loop}
        autoPlay={false}
      />
    </Box>
  );
};

export default LottiePlayer;
